import type { QueryClient } from '@tanstack/react-query';

import { chatMutationKeys, chatQueryKeys } from './queryKeys.ts';

// A message or a click this tab is sending in a chat: its answer brings the chat back.
const CHAT_TURNS = [chatMutationKeys.send, chatMutationKeys.decide];

/**
 * Shows what arrived in a chat without a turn of the user's, such as Finlay's report on a search it started: the chat
 * and the chat list are fetched again. While this tab is sending a message or a click in that chat, the fetch waits
 * until it's over, since its answer, read before the arrival, would land after the fetch and hide it.
 */
export const refreshChat = (queryClient: QueryClient, chatId: string) => {
  const busy = () =>
    CHAT_TURNS.some(
      (mutationKey) =>
        queryClient.isMutating({
          mutationKey,
          predicate: (mutation) =>
            (mutation.state.variables as { chatId?: string } | undefined)
              ?.chatId === chatId,
        }) > 0,
    );
  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: chatQueryKeys.detail(chatId),
    });
    void queryClient.invalidateQueries({ queryKey: chatQueryKeys.lists() });
  };

  if (!busy()) {
    refresh();
    return;
  }
  const unsubscribe = queryClient.getMutationCache().subscribe(() => {
    if (busy()) return;
    unsubscribe();
    refresh();
  });
};
